/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./DashboardSpecUtil'
], function(
	_,
	DashboardSpecUtil
) {

	const WIDGET_TYPE = {
		LIVEWIDGET: 'live',
		FILTER: 'filter'
	};

	const escapeRegExp = (string) => {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
	};

	const replaceItemIdInString = (string = '', oldItemId, newItemId) => {
		const regExpr = new RegExp(escapeRegExp(oldItemId), 'g');
		return string.replace(regExpr, newItemId);
	};

	const replaceIdsInArray = (jsonSpec, collection, index, oldItemId, newItemId) => {
		let string = JSON.stringify(jsonSpec);
		string = replaceItemIdInString(string, oldItemId, newItemId);
		collection[index] = JSON.parse(string);
	};

	class BaseRelinkItemId {
		constructor(jsonSpec, sourceId) {
			if (!jsonSpec) {
				throw new Error ('Invalid JSON specification');
			}
			this.sourceId = sourceId;
			this.jsonSpec = jsonSpec;
		}

		getItemIds () {
			return [];
		}

		replaceItemIds () {
		}

		postReplaceItemIds() {
		}

		validSourceId (idToCheck) {
			return this.sourceId === idToCheck;
		}
	}

	class RelinkPageContext extends BaseRelinkItemId {
		constructor (...args) {
			super(...args);
		}

		getItemIds () {
			let resultSet = [];
			for (const entry of this.jsonSpec) {
				if (this.validSourceId(entry.sourceId) && entry.hierarchyUniqueNames) {
					resultSet = resultSet.concat(entry.hierarchyUniqueNames);
				}
			}

			return resultSet;
		}

		replaceItemIds (oldItemId, newItemId) {
			this.jsonSpec.forEach((entry, index, collection) => {
				if (this.validSourceId(entry.sourceId)) {
					let spec = JSON.stringify(entry);
					spec = replaceItemIdInString(spec, oldItemId, newItemId);
					collection[index] = JSON.parse(spec);
				}
			});
		}
	}

	/**
	 * update mappings
	 * @example
	 * [{
			"name": "Product_line",
			"modelFilterItem": "[Sales (query)].[Products].[Product line]",
			"mapTo": "QC_Data_xlsx.Product_line"
		},
		{
			"name": "Order_Method",
			"modelFilterItem": "[Sales (query)].[Order method].[Order method type]",
			"mapTo": "QC_Data_xlsx.Order_method"
		}]
		Updated "mapTo" part only and we should never change the modelFilterItem part since it comes from report.
		After update the mappings:
		[ {
			"name": "Product_line",
			"modelFilterItem": "[Sales (query)].[Products].[Product line]",
			"mapTo": "dataSetDemo_2_csv.Product_line"
		},
		{
			"name": "Order_Method",
			"modelFilterItem": "[Sales (query)].[Order method].[Order method type]",
			"mapTo": "dataSetDemo_2_csv.Order_method"
		}]
	*/
	class RelinkDrillThrough extends BaseRelinkItemId {
		constructor (...args) {
			super(...args);
		}

		_getAllMappings() {
			return _.flatten(
				this.jsonSpec.map(
					entry => (this.validSourceId(entry.modelRefs[0]) && entry.mappings) || []
				)
			);
		}

		getItemIds () {
			return this._getAllMappings().map(
				({ mapTo }) => mapTo
			).filter(
				itemId => itemId && (itemId !== 'none')
			);
		}

		replaceItemIds (oldItemId, newItemId) {
			this._getAllMappings().forEach(mapping => {
				if (mapping.mapTo === oldItemId) {
					mapping.mapTo = newItemId;
				}
			});
		}
	}

	class RelinkLayout extends BaseRelinkItemId {
		constructor(...args) {
			super(...args);
			this._widgets = this._getWidgetModelsObject(this.jsonSpec);
			this.relinkWidget = new RelinkWidget(this._widgets, this.sourceId);
		}

		getItemIds () {
			return this.relinkWidget.getItemIds();
		}

		replaceItemIds (oldItemId, newItemId) {
			return this.relinkWidget.replaceItemIds(oldItemId, newItemId);
		}

		_getWidgetModelsObject (layout) {
			const o = {};
			//Take list of widget models and convert it to an object keyed by the model ids
			DashboardSpecUtil.getWidgetModelList(layout).forEach(
				widgetModel => o[widgetModel.id] = widgetModel
			);
			return o;
		}

	}

	class RelinkWidget extends BaseRelinkItemId {
		constructor (...args) {
			super(...args);
		}

		getItemIds () {
			let resultSet = [];
			for (const prop in this.jsonSpec) {
				const widget = this.jsonSpec[prop];
				const itemIds = (widget.type === WIDGET_TYPE.LIVEWIDGET ? this._getLiveWidgetItemIds(widget) : this._getFilterWidgetItemIds(widget)) || [];
				resultSet = resultSet.concat(itemIds);
			}
			return resultSet;
		}

		replaceItemIds (oldItemId, newItemId) {
			for (let prop in this.jsonSpec) {
				let updatedWidget;
				let widget = this.jsonSpec[prop];
				if (widget.type === WIDGET_TYPE.LIVEWIDGET) {
					updatedWidget = this._updateLiveWidgetItemIds(widget, oldItemId, newItemId);
				} else {
					updatedWidget = this._updateFilterWidgetItemIds(widget, oldItemId, newItemId);
				}
				this.jsonSpec[prop] = updatedWidget;
			}
		}

		_getLiveWidgetItemIds (widget) {
			let resultSet = [];
			const dataViews = widget.data && widget.data.dataViews ? widget.data && widget.data.dataViews : [];
			let validSourceId = false;
			for(const dataView of dataViews) {
				validSourceId = this.validSourceId(dataView.modelRef);
				if (validSourceId) {
					const dataItems = dataView.dataItems || [];
					for(const dataItem of dataItems) {
						resultSet.push(dataItem.itemId);
					}
				}
			}

			if (validSourceId) {
				resultSet = resultSet.concat(this._getLocalFilter(widget.localFilters || []));
			}
			return resultSet;
		}

		_getLocalFilter(localFilters) {
			return _.pluck(localFilters, 'columnId');
		}

		_getFilterWidgetItemIds (widget) {
			let resultSet = [];
			const properties = widget.properties || {};
			if (this.validSourceId(properties.sourceId)) {
				resultSet.push(properties.itemId);
			}
			return resultSet;
		}

		_updateLiveWidgetItemIds (widget, oldItemId, newItemId) {
			let validDataSource = false;
			let dataViews = widget.data && widget.data.dataViews ? widget.data.dataViews : [];

			dataViews.forEach((dataView, index, collection) => {
				validDataSource = this.validSourceId(dataView.modelRef);
				if (validDataSource) {
					replaceIdsInArray(dataView, collection, index, oldItemId, newItemId);
				}
			});

			if (validDataSource) {
				let localFilters = widget.localFilters || [];
				localFilters.forEach((filter, index, collection) => {
					replaceIdsInArray(filter, collection, index, oldItemId, newItemId);
				});
			}
			return widget;
		}

		_updateFilterWidgetItemIds (widget, oldItemId, newItemId) {
			let properties = widget.properties || {};
			if (this.validSourceId(properties.sourceId)) {
				if (properties.itemId === oldItemId) {
					properties.itemId = newItemId;
				}
			}
			return widget;
		}
	}

	/**
	* datasetShaping is obsolete in R7.
	* During upgrade from R6 to R7, gemini/dashboard/loader/upgrader/waca_shaping.js is responsbile for upgrading this model to an embbedded shaping module.
	* When upgrade from R6 fail, this datasetShaping model still exist in the dashboard spec and therefore relink must handle this case.
	* After relink, the dashboard spec goes through the upgrade process one more time. If succeed the datasetShaping is replaced by the embedded module in dashboard spec
	*/
	class RelinkDatasetShaping extends BaseRelinkItemId {
		constructor (...args) {
			super(...args);
			this.oldCMAssetId = args[2];
			this.newCMAssetId = args[3];
		}

		getItemIds () {
			let resultSet = [];
			for (const prop in this.jsonSpec) {
				const shaping = this.jsonSpec[prop];
				if (shaping.id === this.oldCMAssetId) {
					resultSet = resultSet.concat(this._getFilters(shaping.filters || []));
					resultSet = resultSet.concat(this._getCalculations(shaping.calculations || []));

				}
			}
			return resultSet;
		}

		replaceItemIds (oldItemId, newItemId) {
			let jsonSpec = this.jsonSpec || [];
			jsonSpec.forEach((shaping, index, collection) => {
				if (shaping.id === this.oldCMAssetId) {
					replaceIdsInArray(shaping, collection, index, oldItemId, newItemId);
				}
			});
		}

		postReplaceItemIds () {
			let jsonSpec = this.jsonSpec || [];
			jsonSpec.forEach(shaping => {
				if (shaping.id === this.oldCMAssetId) {
					shaping.id = this.newCMAssetId;
				}
			});
		}

		_getFilters (filters) {
			return _.pluck(filters, 'columnId');
		}

		_getCalculations (calculations) {
			const expr = _.pluck(calculations, 'expr') || [];
			const params = _.flatten(_.pluck(expr, 'params') || []);
			return _.pluck(params, 'col') || [];
		}
	}

	class RelinkConditionalFormatting extends BaseRelinkItemId {
		constructor (...args) {
			super(...args);
		}


		_getConditionalFormattingItemIds(location) {
			let resultSet = [];
			const conditionalFormats = location.content && location.content.conditionalFormatting && location.content.conditionalFormatting.conditionalFormats ?  location.content.conditionalFormatting.conditionalFormats : [];
			for (const conditionalFormat of conditionalFormats) {
				resultSet.push(conditionalFormat.dataItemId);
			}

			return resultSet;
		}

		findDataItem(path) {
			let resultSet = [];
			const items = path.items || [];
			items.forEach(item => {
				if (item.type === 'widget') {
					const itemIds = this._getConditionalFormattingItemIds(item);
					resultSet = resultSet.concat(itemIds);
				} else {
					resultSet = resultSet.concat(this.findDataItem(item));
				}
			});
			return resultSet;
		}

		getItemIds () {
			let resultSet = [];
			const itemIds = this.findDataItem(this.jsonSpec);
			resultSet = resultSet.concat(itemIds);
			return resultSet;
		}


		replaceDataItem (path, oldItemId, newItemId) {
			const items = path.items || [];
			items.forEach(item => {
				if (item.type === 'widget') {
					let conditionalFormats = item.content && item.content.conditionalFormatting && item.content.conditionalFormatting.conditionalFormats ?  item.content.conditionalFormatting.conditionalFormats : [];
					conditionalFormats.forEach((conditionalFormat, index, collection) => {
						replaceIdsInArray(conditionalFormat, collection, index, oldItemId, newItemId);
					});
				} else {
					this.replaceDataItem(item, oldItemId, newItemId);
				}
			});
		}

		replaceItemIds (oldItemId, newItemId) {
			this.replaceDataItem(this.jsonSpec, oldItemId, newItemId);
		}

	}

	let RelinkObjectClass =  {
		'pageContext' : [RelinkPageContext] ,
		'drillThrough' : [RelinkDrillThrough] ,
		'layout': [RelinkLayout, RelinkConditionalFormatting],
		'widgets': [RelinkWidget],
		'datasetShaping': [RelinkDatasetShaping] //@todo Investigate this: is it still needed? }
	};

	class RelinkItemIdInSpec {
		constructor (jsonSpec = {}, options = {}, logger) {

			if (!options.sourceId) {
				throw new Error ('Invalid sourceId provided');
			}

			if (!options.oldCMAssetId) {
				throw new Error ('Invalid oldCMAssetId provided');
			}


			if (!options.newCMAssetId) {
				throw new Error ('Invalid newCMAssetId provided');
			}

			this.sourceId = options.sourceId;
			this.oldCMAssetId = options.oldCMAssetId;
			this.newCMAssetId = options.newCMAssetId;
			this.jsonSpec = jsonSpec;
			this.objectsToRelink = [];
			this.logger = logger;
			this._initialize();

			return {
				getItemIds: this.getItemIds.bind(this),
				replaceItemIds: this.replaceItemIds.bind(this)
			};
		}

		_initialize() {
			const keys = Object.keys(this.jsonSpec);
			keys.forEach(key => {
				const classesToInstantiate = RelinkObjectClass[key];
				if (classesToInstantiate) {
					classesToInstantiate.forEach(
						classToInstantiate => this.objectsToRelink.push(new classToInstantiate(this.jsonSpec[key], this.sourceId, this.oldCMAssetId, this.newCMAssetId))
					);
				}
			});
		}

		getItemIds () {
			if (this.itemIds) {
				return this.itemIds;
			}

			this.itemIds = [];
			this.objectsToRelink.forEach(objectToRelink => {
				this.itemIds = this.itemIds.concat(objectToRelink.getItemIds());
			});
			this.itemIds = _.uniq(this.itemIds);
			return this.itemIds;
		}

		replaceItemIds(newItemIds = []) {
			if (!this.itemIds || this.itemIds.length !== newItemIds.length) {
				throw new Error('Invalid item Ids to replace');
			}

			let nonUpdate_ableItemIds = [];
			const oldItemIds = this.itemIds;

			oldItemIds.forEach((oldItemId, index) => {
				const newItemId = newItemIds[index];
				if (newItemId) {
					this.objectsToRelink.forEach(objectToRelink => {
						objectToRelink.replaceItemIds(oldItemId, newItemId);
					});
				} else {
					nonUpdate_ableItemIds.push(oldItemId);
				}
			});

			this.objectsToRelink.forEach(objectToRelink => {
				objectToRelink.postReplaceItemIds();
			});

			if (nonUpdate_ableItemIds.length > 0) {
				this.logger.info('Cannot update the following columns: \'' + nonUpdate_ableItemIds.join(',') + '\'');
			}
			return this.jsonSpec;
		}

	}

	return RelinkItemIdInSpec;
});
